const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getClientId = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  return user.clientId;
};

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Der opstod en fejl');
    } else {
      throw new Error('Der opstod en fejl');
    }
  }
  
  // Hvis response er tomt (typisk ved DELETE requests)
  const contentType = response.headers.get('content-type');
  if (!contentType || response.status === 204) {
    return null;
  }
  
  return response.json();
};

const api = {
  getWithoutClientId: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}/customer${endpoint}`, {
      credentials: 'include',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  get: async (endpoint) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      credentials: 'include',
      headers
    });
    return handleResponse(response);
  },

  post: async (endpoint, data) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    headers['Content-Type'] = 'application/json';
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  put: async (endpoint, data) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    headers['Content-Type'] = 'application/json';
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  patch: async (endpoint, data) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    headers['Content-Type'] = 'application/json';
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  delete: async (endpoint) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  // Hjælpefunktion til at hente headers med client name
  getHeadersWithClientName: async (clientId) => {
    const headers = {
      'Authorization': `Bearer ${sessionStorage.getItem('token')}`
    };

    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.role === 'admin' || user.role === 'consultant') {
      // Hvis brugeren allerede har klient information i deres session, brug den
      if (user.clientName) {
        headers['X-Client-Name'] = user.clientName;
        return headers;
      }

      try {
        // Hvis ikke, prøv at hente det fra API'et
        const clientResponse = await fetch(`${API_BASE_URL}/admin/clients/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          },
          credentials: 'include'
        });
        
        if (!clientResponse.ok) {
          console.error('Kunne ikke hente klient information:', await clientResponse.text());
          // Hvis vi ikke kan hente klient information, returner headers uden X-Client-Name
          return headers;
        }
        
        const clientData = await clientResponse.json();
        headers['X-Client-Name'] = clientData.name;
      } catch (error) {
        console.error('Fejl ved hentning af klient information:', error);
        // Hvis der opstår en fejl, returner headers uden X-Client-Name
        return headers;
      }
    }

    return headers;
  },

  upload: async (endpoint, file, type, month, year, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('month', month);
    formData.append('year', year);
    if (options.folder_id !== undefined) {
      formData.append('folder_id', options.folder_id);
    }
    if (options.existingFiles) {
      formData.append('existingFiles', JSON.stringify(options.existingFiles));
    }

    try {
      const clientId = getClientId();
      const url = `${API_BASE_URL}/customer/${clientId}/files/upload`;
      const headers = await api.getHeadersWithClientName(clientId);

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Der opstod en fejl ved upload');
        } else {
          throw new Error('Der opstod en uventet fejl ved upload');
        }
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  },

  download: async (endpoint) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}${endpoint}`, {
      headers,
      credentials: 'include'
    });
    
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Der opstod en fejl ved download');
      } else {
        throw new Error('Der opstod en fejl ved download');
      }
    }

    return response.blob();
  },

  downloadAll: async (type, month, year) => {
    try {
      const clientId = getClientId();
      const url = `${API_BASE_URL}/customer/${clientId}/files/download-all?type=${type}&month=${month}&year=${year}`;
      const headers = await api.getHeadersWithClientName(clientId);

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Der opstod en fejl ved download af alle filer');
        } else {
          throw new Error('Der opstod en uventet fejl ved download af alle filer');
        }
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}-${month}-${year}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  },

  getEarliestAllowedDate: async () => {
    const clientId = getClientId();
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}/earliest-allowed-date`, {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return handleResponse(response);
  },

  createPayRun: async (payRun) => {
    return api.post('/pay-runs', payRun);
  },

  downloadExcel: async (endpoint, params) => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    
    // Byg URL med query parametre
    const url = new URL(`${API_BASE_URL}/customer/${clientId}${endpoint}`);
    if (params) {
      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    }
    
    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });
    
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Der opstod en fejl ved download');
      } else {
        throw new Error('Der opstod en fejl ved download');
      }
    }

    return response.blob();
  },

  // Employee API calls
  getEmployees: async () => {
    return api.get('/employees');
  },

  getPendingEmployees: async () => {
    return api.get('/employees/pending');
  },

  getEmployee: async (id) => {
    return api.get(`/employees/${id}`);
  },

  createEmployee: async (employee) => {
    return api.post('/employees', employee);
  },

  updateEmployee: async (id, employee) => {
    return api.put(`/employees/${id}`, employee);
  },

  deleteEmployee: async (id) => {
    return api.delete(`/employees/${id}`);
  },

  // Employee Template API calls
  getEmployeeTemplates: async () => {
    return api.get('/employees/templates');
  },

  getEmployeeTemplate: async (id) => {
    return api.get(`/employees/templates/${id}`);
  },

  createEmployeeTemplate: async (data) => {
    return api.post('/employees/templates', data);
  },

  updateEmployeeTemplate: async (id, data) => {
    return api.put(`/employees/templates/${id}`, data);
  },

  deleteEmployeeTemplate: async (id) => {
    return api.delete(`/employees/templates/${id}`);
  },

  // Copy from template or employee
  copyFromTemplateOrEmployee: async (type, id) => {
    return api.get(`/employees/copy-from/${type}/${id}`);
  },

  downloadEmployeeList: async () => {
    const clientId = getClientId();
    const headers = await api.getHeadersWithClientName(clientId);
    
    const response = await fetch(`${API_BASE_URL}/customer/${clientId}/employees/download`, {
      headers,
      credentials: 'include'
    });
    
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Der opstod en fejl ved download af medarbejderliste');
      } else {
        throw new Error('Der opstod en fejl ved download af medarbejderliste');
      }
    }

    // Hent blob data
    const blob = await response.blob();
    
    // Start download via browser
    const urlObject = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlObject;
    link.download = 'medarbejdere.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlObject);
  },

  // Folder API calls
  getFolders: async (type, month, year) => {
    return api.get(`/files/folders?type=${type}&month=${month}&year=${year}`);
  },

  createFolder: async (folderData) => {
    return api.post('/files/folders', {
      name: folderData.name,
      type: folderData.type,
      parent_id: folderData.parent_id,
      created_by: folderData.created_by,
      month: folderData.month,
      year: folderData.year
    });
  },

  deleteFolder: async (folderId) => {
    return api.delete(`/files/folders/${folderId}`);
  },

  // Hent filer
  getFiles: async (type, month, year) => {
    return api.get(`/files?type=${type}&month=${month}&year=${year}`);
  },

  updateFile: async (fileId, updateData) => {
    return api.put(`/files/${fileId}`, updateData);
  },

  // Payroll Types API calls
  getPayrollTypes: async () => {
    return api.get('/payroll-types');
  },

  createPayrollType: async (payrollType) => {
    return api.post('/payroll-types', payrollType);
  },

  updatePayrollType: async (id, payrollType) => {
    return api.put(`/payroll-types/${id}`, payrollType);
  },

  deletePayrollType: async (id) => {
    return api.delete(`/payroll-types/${id}`);
  },

  // Opdater status på pay_run
  updatePayRunStatus: async (payRunId, data) => {
    return api.patch(`/pay-runs/${payRunId}/payroll_status`, data);
  }
};

export default api; 